import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Benutzer, Team, TeamEingabe } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBeforeUnload, useFormularZustand } from "@kundeninfo/ui";
import { Chip, Formular, Hinweis, Optionen, Schalter } from "@kundeninfo/ui/me-design";
import { Meldung, meldungAus } from "./gemeinsam.js";
import { KiKnopf } from "../../components/meHuellen.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich TEAMS (Spec Tickets, Nachtrag 1e, F131, Entscheide 210, 215).
 *
 * **Ohne zweite Ebene** — Muster „Ansprechpartner-Rollen": Ein Team hat vier Felder (Name, Kürzel, Aktiv,
 * Mitglieder), und alle stehen schon in seiner Zeile. Angelegt und geändert wird im strikten Modal (Hausregel
 * „Modal-Bearbeitung": Kreuz, Speichern/Abbrechen, Rückfrage bei Ungespeichertem). Gelöscht wird nie, nur
 * stillgelegt (`fk_ticket_team` ist RESTRICT).
 *
 * **Mitglieder** als `Optionen mehrfach` (me-optionen): je aktiver Benutzer ein Kästchen; ein gesperrter steht
 * nur da, solange er Mitglied ist, mit „(gesperrt)" — damit man ihn austragen kann. Wer ausgetragen wird, verliert
 * beim Speichern Zuständigkeit, Beobachtung und Benachrichtigungen an den privaten Tickets des Teams (F133, der
 * Server räumt auf); der Hinweis im Modal sagt das.
 *
 * **CSS:** Block `vw__` (views/Verwaltung.css), wie die übrigen Bereiche.
 */

export type TeamsClient = Pick<ApiClient, "listTeams" | "createTeam" | "updateTeam" | "listUsers">;

type TeamDialog = {
  /** null = neu anlegen. */
  id: string | null; version: number; name: string; shortCode: string; active: boolean; memberIds: string[];
};

const LEERER_TEAMDIALOG: TeamDialog = { id: null, version: 0, name: "", shortCode: "", active: true, memberIds: [] };

type TeamFeld = "name" | "shortCode" | "memberIds";
const TEAM_FELDER: readonly TeamFeld[] = ["name", "shortCode", "memberIds"];

function teamdialogAus(t: Team | null): TeamDialog {
  if (t === null) return LEERER_TEAMDIALOG;
  return { id: t.id, version: t.version, name: t.name, shortCode: t.shortCode, active: t.active,
           memberIds: t.members.map((m) => m.id) };
}

function teamdialogGleich(a: TeamDialog, b: TeamDialog): boolean {
  return a.id === b.id && a.version === b.version && a.name === b.name && a.shortCode === b.shortCode
    && a.active === b.active && [...a.memberIds].sort().join() === [...b.memberIds].sort().join();
}

export function TeamsBereich({ client }: { client: TeamsClient }): ReactElement {
  const [zeilen, setZeilen] = useState<Team[] | null>(null);
  const [nutzer, setNutzer] = useState<Benutzer[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<TeamDialog | null>(null);
  /** Eine Auskunft nach „Neu laden“: Das geöffnete Team gibt es nicht mehr. */
  const [auskunft, setAuskunft] = useState<string | null>(null);

  const listTeams = client.listTeams;
  const listUsers = client.listUsers;

  useEffect(() => {
    let abgebrochen = false;
    Promise.all([listTeams(), listUsers()])
      .then(([teams, benutzer]) => {
        if (abgebrochen) return;
        setZeilen(teams.items);
        setNutzer(benutzer.items);
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) setLadeFehler(meldungAus(fehler, "Die Teams konnten nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [listTeams, listUsers]);

  /** Wie bei den Rollen: 422 am Feld (Fokus springt hin), 409 als „fremder Stand" mit „Neu laden". */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Kein Team offen.");
      const eingabe: TeamEingabe = { name: d.name, shortCode: d.shortCode, active: d.active, memberIds: d.memberIds };
      if (d.id === null) await client.createTeam(eingabe);
      else await client.updateTeam(d.id, d.version, eingabe);
      // Neu lesen: Reihenfolge (aktive zuerst, Name) und Mitgliedernamen kommen vom Server.
      // Gespeichert ist dann schon: ein Lesefehler ist ein Ladefehler, kein Speicherfehler
      // (sonst endete das Wiederholen in 422/409).
      try {
        setZeilen((await client.listTeams()).items);
      } catch (fehler: unknown) {
        setLadeFehler(meldungAus(fehler, "Die Teams konnten nicht geladen werden."));
      }
      return true;
    },
    neuLaden: async () => {
      const [antwort, benutzer] = await Promise.all([client.listTeams(), client.listUsers()]);
      setZeilen(antwort.items);
      setNutzer(benutzer.items);
      const d = dialog;
      if (d === null || d.id === null) return;
      const frisch = antwort.items.find((t) => t.id === d.id);
      if (frisch === undefined) {
        setDialog(null);
        setAuskunft("Dieses Team gibt es nicht mehr.");
        return;
      }
      setDialog(teamdialogAus(frisch));
    },
    bekannteFelder: TEAM_FELDER,
    beschriftungen: { name: "Name", shortCode: "Kürzel", memberIds: "Mitglieder" },
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<TeamFeld, string>>;

  const dialogGeaendert = dialog !== null && !teamdialogGleich(
    dialog, teamdialogAus(zeilen?.find((t) => t.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  async function abbrechen(): Promise<void> {
    if (arbeitet) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    setDialog(null);
  }

  function oeffnen(t: Team | null): void {
    formular.zuruecksetzen();
    setAuskunft(null);
    setDialog(teamdialogAus(t));
  }

  async function speichern(): Promise<void> {
    if (await formular.aktion() === true) setDialog(null);
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  // Wählbar sind aktive Benutzer; gesperrte nur, solange sie Mitglied sind (zum Austragen).
  const mitglieder = dialog === null ? [] : nutzer
    .filter((u) => u.active || dialog.memberIds.includes(u.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {auskunft !== null && <Hinweis art="info">{auskunft}</Hinweis>}
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => oeffnen(null)}>
          Neues Team
        </KiKnopf>
      </div>

      {zeilen.length === 0 && (
        <p className="vw__hinweis">Noch kein Team. „Neues Team“ legt eines an, etwa die Buchhaltung.</p>
      )}
      <ul className="vw__liste">
        {zeilen.map((t) => (
          <li key={t.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">{t.name}</span>
              <Chip className="vw__marke">{t.shortCode}</Chip>
              {!t.active && <Chip className="vw__marke">stillgelegt</Chip>}
            </div>
            <span className="vw__eintrag-zusatz">
              {t.members.length === 0 ? "keine Mitglieder" : t.members.map((m) => m.name).join(", ")}
            </span>
            <div className="vw__eintrag-aktionen">
              <KiKnopf name={`${t.name} ändern`} onClick={() => oeffnen(t)}>
                Ändern
              </KiKnopf>
            </div>
          </li>
        ))}
      </ul>

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Neues Team" : "Team ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? (
              <span>
                Dieses Team wurde inzwischen geändert. Deine Eingaben stehen noch da —
                „Neu laden" übernimmt den aktuellen Stand.
              </span>
            ) : undefined}>
            <Feld id="vw-t-name" label="Name" bearbeitbar wert={dialog.name}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, name: w }))}
              {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
            <Feld id="vw-t-kuerzel" label="Kürzel" bearbeitbar wert={dialog.shortCode}
              hilfe="1 bis 8 Zeichen aus A–Z und 0–9; steht als Chip an privaten Tickets."
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, shortCode: w }))}
              {...(feldFehler.shortCode !== undefined ? { fehler: feldFehler.shortCode } : {})} />
            <Schalter id="vw-t-active" beschriftung="Aktiv (für neue Zuordnungen wählbar)"
              an={dialog.active}
              onGeaendert={(e) => setDialog((d) => (d === null ? d : { ...d, active: e.an }))} />
            {/* Kennungen sind UUIDs — ohne Komma, wie `Optionen mehrfach` es verlangt. */}
            <Optionen beschriftung="Mitglieder" mehrfach wert={dialog.memberIds}
              hilfe="Mitglieder und die Administration sehen die privaten Tickets des Teams. Wer ausgetragen wird, verliert dort beim Speichern Zuständigkeit und Beobachtung."
              {...(feldFehler.memberIds !== undefined ? { fehler: feldFehler.memberIds } : {})}
              onGeaendert={(e) => setDialog((d) => (d === null ? d : { ...d, memberIds: e.werte ?? [] }))}>
              {mitglieder.map((u) => (
                <option key={u.id} value={u.id}>{u.active ? u.name : `${u.name} (gesperrt)`}</option>
              ))}
            </Optionen>
          </Formular>
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={arbeitet}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
